import React, { useRef, useState } from "react";
import styles from "./file-upload-field.module.scss";
import {
	UploadFileIcon,
	TrashCanFileIcon,
	ErrorInfoIcon,
	JpgFormatIcon,
	PngFormatIcon,
	PdfFormatIcon,
	XlsxIcon,
	DocxFormatIcon,
	MoreFormatIcon,
} from "../../../../../assets/icons";

interface FileUploadFieldProps {
	file?: File | null;
	fileName?: string;
	onFileSelected: (file: File) => void;
	onCleared: () => void;
	allowedExtensions?: string[];
	maxFileSizeBytes?: number;
	hasError?: boolean;
}

const formatFileSize = (bytes: number): string => {
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatMaxFileSize = (bytes: number): string => {
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
	return `${Math.round(bytes / (1024 * 1024))} MB`;
};

const formatExtensionsList = (exts: string[]): string => {
	const upper = exts.map(e => e.toUpperCase());
	if (upper.length <= 1) return upper[0] ?? "";
	return `${upper.slice(0, -1).join(", ")} or ${upper[upper.length - 1]}`;
};

const getFormatIcon = (extension?: string): React.FC<React.SVGProps<SVGSVGElement>> => {
	switch (extension?.toLowerCase()) {
		case "jpg":
		case "jpeg":
			return JpgFormatIcon;
		case "png":
			return PngFormatIcon;
		case "pdf":
			return PdfFormatIcon;
		case "xls":
		case "xlsx":
			return XlsxIcon;
		case "docx":
			return DocxFormatIcon;
		default:
			return MoreFormatIcon;
	}
};

const FileUploadField: React.FC<FileUploadFieldProps> = ({
	file,
	fileName,
	onFileSelected,
	onCleared,
	allowedExtensions = [],
	maxFileSizeBytes,
	hasError = false,
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [localError, setLocalError] = useState<string | null>(null);

	const extensionsLower = allowedExtensions.map(e => e.toLowerCase());
	const acceptString = extensionsLower.map(e => `.${e}`).join(",");
	const displayName = file?.name ?? fileName;
	const fileExtension = displayName?.includes(".") ? displayName.split(".").pop()?.toLowerCase() : undefined;

	const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const selected = e.target.files?.[0];
		e.target.value = ""; // allow re-selecting the same file
		if (!selected) return;

		const ext = selected.name.split(".").pop()?.toLowerCase() ?? "";
		if (extensionsLower.length > 0 && !extensionsLower.includes(ext)) {
			setLocalError(`Invalid file type. Allowed: ${allowedExtensions.join(", ")}`);
			return;
		}
		if (maxFileSizeBytes && selected.size > maxFileSizeBytes) {
			setLocalError(`File too large. Max ${formatMaxFileSize(maxFileSizeBytes)}`);
			return;
		}
		setLocalError(null);
		onFileSelected(selected);
	};

	const openPicker = () => inputRef.current?.click();

	const subtitleText = `${formatExtensionsList(allowedExtensions)}${
		maxFileSizeBytes ? ` — Less than ${formatMaxFileSize(maxFileSizeBytes)}` : ""
	}`;

	const infoText = [file ? formatFileSize(file.size) : "", fileExtension ? fileExtension.toUpperCase() : ""]
		.filter(Boolean)
		.join(" · ");

	const FormatIcon = getFormatIcon(fileExtension);
	const showError = hasError || !!localError;

	return (
		<div>
			<div
				className={`${styles.container} ${showError ? styles.error : ""}`}
				role="button"
				tabIndex={0}
				onClick={openPicker}
				onKeyDown={e => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						openPicker();
					}
				}}
			>
				{displayName ? (
					<div className={styles.row}>
						<FormatIcon className={styles.fileIcon} />
						<div className={styles.textCol}>
							<span className={styles.fileName}>{displayName}</span>
							{infoText && <span className={styles.metaText}>{infoText}</span>}
						</div>
						<button
							type="button"
							className={styles.deleteButton}
							onClick={e => {
								e.stopPropagation();
								onCleared();
							}}
							aria-label="Remove file"
						>
							<TrashCanFileIcon className={styles.deleteIcon} />
						</button>
					</div>
				) : (
					<div className={styles.row}>
						<span className={styles.uploadIconWrap}>
							<UploadFileIcon className={styles.uploadIcon} />
						</span>
						<div className={styles.textCol}>
							<span className={styles.uploadTitle}>Upload File</span>
							<span className={styles.metaText}>{subtitleText}</span>
						</div>
					</div>
				)}
			</div>

			{localError && (
				<div className={styles.errorRow}>
					<ErrorInfoIcon className={styles.errorIcon} />
					<span className={styles.errorText}>{localError}</span>
				</div>
			)}

			<input ref={inputRef} type="file" accept={acceptString || undefined} className={styles.hiddenInput} onChange={handleSelect} />
		</div>
	);
};

export default FileUploadField;
